"use client";

import {
  FC,
  memo,
  ReactNode,
  useEffect,
  useState,
  Suspense,
} from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useSignupFlowReset } from "apps/website/hooks/useSignupFlowReset";
import DefaultHead from "apps/website/components/page/DefaultHead/DefaultHead";
import { makePageTags } from "apps/website/utils/website/head";
import Header from "apps/website/components/page/Header/Header";
import {
  HEADER_LINKS_NONE,
} from "apps/website/components/page/Header/Header.map";
import type { TDiscount } from "@./suf-store";
import {
  useSignupFlowStore,
  useFreshSignupFlowNavigation,
  SIGNUP_FLOW_PAGES,
} from "@./state";
import {
  SignupFlowSlug,
} from "libs/state/src/lib/stores/flows/fresh/signupFlow/constants";
import { getInterpolationVariables } from "libs/utils/sign-up/fresh";
import type { Gender } from "@./cat-calculations";
import { DiscountCodeType } from "@./suf-store";

import {
  formatDiscount,
} from "../../components/pages/flows/fresh/sign-up/Pricing/utils";
import Main from "../../components/layout/Main/Main";
import Footer from "../../components/page/Footer/Footer";
import { themeRootClassMap } from "../../maps/Theme.map";
import cx from "../../utils/misc/cx";
import {
  QueryParamHandler,
} from "../../components/flows/fresh/sign-up/QueryParamHandler/QueryParamHandler";
import {
  SignupLoadingScreen,
} from "../../components/flows/fresh/common/SignupLoadingScreen/SignupLoadingScreen";
import {
  ProgressSteps,
} from "../../components/flows/fresh/common/ProgressSteps/ProgressSteps";
import {
  signupSteps,
} from "../../components/feature/Navigation/SimpleProgressSteps/constants/signupSteps";
import {
  TitleSection,
} from "../../components/flows/fresh/sign-up/TitleSection/TitleSection";
import SignupPromotionalBanner
  from "../../components/flows/fresh/sign-up/SignupPromotionalBanner/SignupPromotionalBanner";



import { ILayout } from "../layouts.map";
import { TFunctionOf, TranslationKeyOf } from "apps/website/types/i18n";

export type SignupFlowLayoutProps = Partial<ILayout>;

const INITIAL_SLUG = SignupFlowSlug.CAT_COUNT;
const LOGIN_LINK = { id: "g-0", name: "Login", to: "/login" };

const checkShouldRedirect = (
  isStoreInitialised: boolean,
  pathname: string,
  versionMismatchDetected: boolean,
  activeCatIdsLength: number,
): boolean => isStoreInitialised &&
  pathname !== INITIAL_SLUG &&
  (versionMismatchDetected || activeCatIdsLength === 0);

const getAnnouncementText = (
  discount: TDiscount,
  bannerT: TFunctionOf<"Forms.Fresh.SignUp.PromotionalBanner">,
): string => {
  const formattedDiscount = formatDiscount(discount);
  if (!formattedDiscount) {
    return bannerT("announcementFallback");
  }
  return discount.type === DiscountCodeType.ABSOLUTE_TOTAL
    ? bannerT("announcementAbsolute", { discount: formattedDiscount })
    : bannerT("announcementOff", { discount: formattedDiscount });
};

const resolvePageTitle = (
  title: string | undefined,
  currentCat: { name?: string; gender?: Gender } | undefined,
  t: TFunctionOf<"Forms.Fresh.SignUp.Titles">,
  catCount: number,
): string => {
  if (title && currentCat) {
    const variables = getInterpolationVariables({ name: currentCat.name, gender: currentCat.gender });
    const key = title === "checkout" && catCount === 1
      ? "checkoutSingleCat"
      : title;
    return t(key as TranslationKeyOf<"Forms.Fresh.SignUp.Titles">, variables);
  }
  return t(title as TranslationKeyOf<"Forms.Fresh.SignUp.Titles">) || "";
};

interface SignupFlowContentProps {
  showPromoBanner: boolean;
  announcementText: string;
  promoBannerAnimateOnEntry: boolean;
  hideProgressSteps?: boolean;
  pathname: SignupFlowSlug;
  progressBarBgClass?: string;
  title?: string;
  getTitle: () => string;
  children: ReactNode;
}

const SignupFlowContent: FC<Readonly<SignupFlowContentProps>> = ({
  showPromoBanner,
  announcementText,
  promoBannerAnimateOnEntry,
  hideProgressSteps,
  pathname,
  progressBarBgClass,
  title,
  getTitle,
  children,
}) => (
  <>
    <div className="fixed top-[var(--header-height-mobile)] xl:top-[var(--header-height-desktop)] w-full z-20 bg-white">
      { showPromoBanner && (
        <SignupPromotionalBanner
          announcements={[
            {
              text: announcementText,
              showTag: true,
            },
          ]}
          theme="pink"
          animateOnEntry={promoBannerAnimateOnEntry}
        />
      ) }
      <ProgressSteps
        hideProgressSteps={hideProgressSteps}
        pathname={pathname}
        steps={signupSteps}
        bgColor={progressBarBgClass}
      />
    </div>
    { showPromoBanner && (
      <div className="h-[44px] xl:h-[60px] w-full" />
    ) }
    <TitleSection
      title={title}
      hideProgressSteps={hideProgressSteps}
      getTitle={getTitle}
    />
    { children }
  </>
);

const FreshSignupFlow: FC<Readonly<SignupFlowLayoutProps>> =
  ({ children, head, lastBuilt }) => {
    const t = useTranslations("Forms.Fresh.SignUp.Titles");
    const bannerT = useTranslations("Forms.Fresh.SignUp.PromotionalBanner");
    const actionsT = useTranslations("Shared.Actions");
    const pathname = usePathname();
    const [ hideBackButton, setHideBackButton ] = useState(true);
    const [ mounted, setMounted ] = useState(false);

    const { canGoBack, goBack } = useFreshSignupFlowNavigation(pathname as SignupFlowSlug);

    const {
      cats,
      activeCatId,
      isStoreInitialised,
      versionMismatchDetected,
      getActiveCatIds,
      discount,
      isTransitionVisible,
    } = useSignupFlowStore((state) => ({
      activeCatId: state.activeCatId,
      cats: state.cats,
      isStoreInitialised: state.isStoreInitialised,
      versionMismatchDetected: state.versionMismatchDetected,
      getActiveCatIds: state.getActiveCatIds,
      discount: state.subscription.discount,
      isTransitionVisible: state.viewState.catParentDetails?.isTransitionVisible ?? false,
    }));

    // Synchronously check if we should redirect (prevents rendering with invalid state)
    const shouldRedirect = checkShouldRedirect(
      isStoreInitialised,
      pathname,
      versionMismatchDetected,
      getActiveCatIds().length,
    );

    const currentCat = cats[activeCatId];

    const {
      title,
      hideProgressSteps,
      hideFooter = true,
      bgColor = "light-grey",
      headerBgColor = "light",
      hidePromoBanner = false,
      promoBannerAnimateOnEntry = false,
      progressBarBgColor,
    } = SIGNUP_FLOW_PAGES[pathname as SignupFlowSlug];

    useEffect(() => {
      setMounted(true);
    }, []);

    useEffect(() => {
      setHideBackButton(pathname === INITIAL_SLUG);
    }, [ pathname ]);

    useSignupFlowReset();

    const showPromoBanner = !hidePromoBanner && !isTransitionVisible;
    const progressBarBgClass = progressBarBgColor ? themeRootClassMap[progressBarBgColor] : undefined;
    const announcementText = getAnnouncementText(discount, bannerT);

    return (
      <div className="overflow-x-hidden">
        <DefaultHead
          seo={makePageTags(head?.seo)}
          /* @ts-ignore */
          hideFromSearch={head?.hide_from_search_engines}
          lastBuilt={lastBuilt}
          fallbackTags={head?.fallbackTags && makePageTags(head.fallbackTags)}
        />
        <Suspense>
          <QueryParamHandler />
        </Suspense>

        <Header
          {...HEADER_LINKS_NONE}
          hasAnnouncementBanner={false}
          guestLinks={hideBackButton ? [ LOGIN_LINK ] : []}
          historyNavigation={{
            back: () => (canGoBack ? goBack() : undefined),
            hide: hideBackButton,
          }}
          backgroundColor={headerBgColor}
        />

        <Main className={cx(themeRootClassMap[bgColor], "min-h-[calc(100dvh-var(--header-height-mobile))] xl:min-h-[calc(100dvh-var(--header-height-desktop))]")}>
          { !mounted || !isStoreInitialised || shouldRedirect ? (
            <SignupLoadingScreen t={actionsT} />
          ) : (
            <SignupFlowContent
              showPromoBanner={showPromoBanner}
              announcementText={announcementText}
              promoBannerAnimateOnEntry={promoBannerAnimateOnEntry}
              hideProgressSteps={hideProgressSteps}
              pathname={pathname as SignupFlowSlug}
              progressBarBgClass={progressBarBgClass}
              title={title}
              getTitle={() => resolvePageTitle(title, currentCat, t, getActiveCatIds().length)}
            >
              { children }
            </SignupFlowContent>
          ) }
        </Main>

        { !hideFooter && (
          <Footer />
        ) }
      </div>
    );
  };

export default memo(FreshSignupFlow);
